import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import { toast } from 'sonner';
import { ClipboardCheck, Loader2, CheckCircle } from 'lucide-react';
import { sevBadge, CostBadge, ScoreRing, handleApiError } from './shared';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OPTIONS = [
  { v: 'implemented', l: 'Implemented', c: 'border-green-500 bg-green-500/10 text-green-500' },
  { v: 'partial', l: 'Partial', c: 'border-yellow-500 bg-yellow-500/10 text-yellow-500' },
  { v: 'not_implemented', l: 'Not implemented', c: 'border-red-500 bg-red-500/10 text-red-500' },
  { v: 'not_applicable', l: 'N/A', c: 'border-muted-foreground bg-muted text-muted-foreground' },
];

export const SecurityAssurance = ({ locked, cost, onComplete }) => {
  const navigate = useNavigate();
  const [frameworks, setFrameworks] = useState([]);
  const [fwId, setFwId] = useState('nist_csf');
  const [org, setOrg] = useState({ org_name: '', scope: '' });
  const [responses, setResponses] = useState({});
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => { axios.get(`${API}/org-security/assurance/frameworks`, { withCredentials: true }).then(r => setFrameworks(r.data)).catch(() => {}); }, []);

  const fw = frameworks.find(f => f.id === fwId);
  const answered = fw ? fw.controls.filter(c => responses[c.id]).length : 0;

  const assess = async () => {
    if (!org.org_name) { toast.error('Organization name is required'); return; }
    setRunning(true);
    try {
      const r = await axios.post(`${API}/org-security/assurance/assess`, { framework: fwId, ...org, responses }, { withCredentials: true });
      setResult(r.data); onComplete?.(); toast.success(`Assurance score: ${r.data.score}/100 (${r.data.maturity})`);
    } catch (err) { handleApiError(err, toast, navigate); } finally { setRunning(false); }
  };

  return (
    <div className="space-y-6" data-testid="assurance-module">
      <Card>
        <CardHeader className="pb-3"><CardTitle className="text-base font-heading flex items-center gap-2"><ClipboardCheck className="h-4 w-4 text-primary" /> Security assurance assessment</CardTitle></CardHeader>
        <CardContent className="space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {frameworks.map(f => (
              <button key={f.id} onClick={() => { setFwId(f.id); setResponses({}); }} className={`p-3.5 rounded-xl border text-left transition-all ${fwId === f.id ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/30'}`} data-testid={`framework-${f.id}`}>
                <p className="text-sm font-semibold">{f.name}</p><p className="text-[11px] text-muted-foreground mt-0.5">{f.controls.length} controls</p>
              </button>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div><Label className="mb-2 block">Organization</Label><Input value={org.org_name} onChange={e => setOrg(o => ({ ...o, org_name: e.target.value }))} placeholder="Acme Corp" data-testid="assurance-org-input" /></div>
            <div><Label className="mb-2 block">Scope (optional)</Label><Input value={org.scope} onChange={e => setOrg(o => ({ ...o, scope: e.target.value }))} placeholder="Production cloud environment" data-testid="assurance-scope-input" /></div>
          </div>
          {fw && (
            <div className="space-y-2">
              <div className="flex items-center justify-between text-xs text-muted-foreground"><span>Control status</span><span data-testid="assurance-progress">{answered}/{fw.controls.length} answered</span></div>
              <Progress value={(answered / fw.controls.length) * 100} className="h-1.5" />
              {fw.controls.map(c => (
                <div key={c.id} className="p-3 rounded-lg border bg-card flex flex-col md:flex-row md:items-center gap-3" data-testid={`assurance-control-${c.id}`}>
                  <div className="flex-1 min-w-0"><p className="text-sm font-medium"><span className="font-mono text-primary text-xs mr-2">{c.id}</span>{c.name}</p><p className="text-[11px] text-muted-foreground">{c.domain} · weight {c.weight}</p></div>
                  <div className="flex gap-1.5 flex-wrap">
                    {OPTIONS.map(o => (
                      <button key={o.v} disabled={locked} onClick={() => setResponses(r => ({ ...r, [c.id]: o.v }))} className={`px-2.5 py-1 rounded-md border text-[11px] transition-colors ${responses[c.id] === o.v ? o.c : 'border-border text-muted-foreground hover:border-primary/40'}`} data-testid={`assurance-${c.id}-${o.v}`}>{o.l}</button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          <Button onClick={assess} disabled={locked || running} className="gap-2" data-testid="assurance-assess-btn">{running ? <Loader2 className="h-4 w-4 animate-spin" /> : <ClipboardCheck className="h-4 w-4" />} Generate assurance report <CostBadge cost={cost} /></Button>
        </CardContent>
      </Card>

      {result && (
        <Card className="border-primary/30" data-testid="assurance-result">
          <CardHeader className="pb-3"><CardTitle className="text-base font-heading">{result.framework_name} — {result.name}</CardTitle></CardHeader>
          <CardContent className="space-y-5">
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-center">
              <ScoreRing score={result.score} label={`Maturity: ${result.maturity}`} />
              {['high', 'medium', 'low'].map(s => <div key={s} className={`text-center p-3 rounded-lg ${sevBadge[s]}`}><p className="text-xl font-bold">{result.severity_counts[s]}</p><p className="text-[10px] capitalize">{s} gaps</p></div>)}
              <div className="text-center p-3 rounded-lg bg-green-500/10 text-green-500"><p className="text-xl font-bold">{result.total_controls - result.gaps.length}</p><p className="text-[10px]">Satisfied</p></div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {Object.entries(result.domain_scores).map(([d, v]) => <div key={d} className="p-2.5 rounded-lg border text-xs space-y-1.5"><div className="flex justify-between"><span>{d}</span><span className="font-mono">{v}%</span></div><Progress value={v} className="h-1" /></div>)}
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium">Control gaps ({result.gaps.length})</p>
              {result.gaps.map(g => (
                <div key={g.id} className="p-3 rounded-lg border bg-card" data-testid={`assurance-gap-${g.id}`}>
                  <div className="flex items-center gap-2 mb-1"><Badge variant="outline" className={`text-[9px] uppercase ${sevBadge[g.severity]}`}>{g.severity}</Badge><p className="text-sm font-medium"><span className="font-mono text-xs mr-2 text-primary">{g.id}</span>{g.name}</p><Badge variant="outline" className="ml-auto text-[10px] capitalize">{g.status.replace('_', ' ')}</Badge></div>
                  <p className="text-xs text-muted-foreground">{g.recommendation}</p>
                </div>
              ))}
              {result.gaps.length === 0 && <p className="text-sm text-green-500 flex items-center gap-2"><CheckCircle className="h-4 w-4" /> No gaps — all in-scope controls implemented.</p>}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
